import moment from 'moment';
import { paymentPlansApi } from './api';
import { select } from '../shared/utils/dom';
import { delimiters } from '../shared/constants';
import formMixin from '../shared/vue/mixins/form';
import multistepMixin from '../shared/vue/mixins/multistep';
import { createError, removeError } from '../shared/formErrors';
import { formatBasicCurrency } from '../shared/utils/misc';
import dataLayerPush from '../shared/utils/analytics';


/**
 * Enrolling in payment plan
 */
export default function paymentPlan() {
  const el = select('[data-vue-app="payment-plan-enroll"]');

  if (el) {
    const app = new Vue({ // eslint-disable-line no-unused-vars
      el,
      delimiters,
      mixins: [formMixin, multistepMixin],
      data: {
        agree: false,
        isReady: false,
        success: false,
        error: false,
        errorMessage: null,
        // plan details
        accountId: '',
        initialPaymentAmount: '',
        initialPaymentDueDate: '',
        nextPaymentDate: '',
        selectPaymentFrequency: 0,
        paymentFrequencyOptions: '',
        selectedPlan: '',
        // recalc info
        defaultPaymentAmount: '',
        // for accessibility in live regions
        liveText: '',
      },
      computed: {
        enrollmentDataStructure() {
          return {
            planId: this.selectedPlan,
            initialPaymentAmount: parseFloat(this.initialPaymentAmount.replace(/[^0-9-.]/g, '')),
            initialPaymentDueDate: moment(this.initialPaymentDueDate).format("YYYY-MM-DD"),
            termPeriodMonths: this.frequencyDetails.termPeriodMonths,
            monthlyPayment: this.frequencyDetails.paymentAmount,
            totalAgreementAmount: this.frequencyDetails.totalAgreementAmount ? parseFloat(this.frequencyDetails.totalAgreementAmount.replace(/[^0-9-.]/g, '')) : 0,
          }
        },
        frequencyDetails() {
          // return the frequency details based on the index
          // index being the value in the dropdown
          // or default to the first one for plans with only one option
          const plan = this.paymentFrequencyOptions[this.selectPaymentFrequency];
          const selectedFrequency = {
            termPeriodMonths: plan.termPeriodMonths,
            paymentAmount: plan.paymentAmount,
            totalAgreementAmount: formatBasicCurrency(plan.totalAgreementAmount),
          }
          return selectedFrequency;
        },
        // dynamic component for deciding which details to show
        planDetailsComponent() {
          if (this.selectedPlan === 'LV') {
            return 'pay-plan-details-cma';
          } else if (this.selectedPlan === 'SD' || this.selectedPlan === 'SE') {
            return 'pay-plan-details-nipsco';
          }
          // show basic plan details if it's not a CMA or NIPSCO plan
          return 'pay-plan-details-basic'
        },
        // show Two, Three and Four Month Levelized plan
        showCmaPlanInfo() {
          return this.selectedPlan === 'LV';
        },
        // show Three Month Standard plan
        showNipscoPlanInfo() {
          return this.selectedPlan === 'SD' || this.selectedPlan === 'SE';
        },
      },
      watch: {
        step(newVal, oldVal) { // eslint-disable-line no-unused-vars
          /**
           * Changes live text depending on the form step
           */
          if (this.step === 2) {
            this.liveText = 'Submitting the enrollment';
          } else if (this.step === 1) {
            this.liveText = 'You are now back on Step 1 to review your payment plan terms and details';
          }
        },
      },
      mounted() {
        this.loading = true;
        Vue.nextTick(() => {
          // get the plan selected
          this.loadSelectedPlan();
        });
      },
      /**
       * Methods
       */
      methods: {
        // call to enroll the user
        enrollPaymentPlan() {
          this.loading = true;
          // get enrollment data ready for API request
          const enrollmentData = this.enrollmentDataStructure;
          paymentPlansApi.enrollInPaymentPlan(enrollmentData)
            .then(() => {
              this.loading = false;
              this.setStep(3);
              this.success = true;
              dataLayerPush('PaymentPlanEnroll');
            })
            .catch(() => {
              this.loading = false;
              this.setStep(3);
              this.error = true;
            });
        },
        // get the plan details from the API
        getPaymentPlanDetails() {
          paymentPlansApi.getPlanDetail(this.selectedPlan)
            .then((response) => {
              this.accountId = response.data.accountId;
              this.initialPaymentAmount = formatBasicCurrency(response.data.initialPaymentAmount);
              // default is used to store the initial payment amount to check recalculation information for nipsco
              this.defaultPaymentAmount = response.data.initialPaymentAmount;
              this.initialPaymentDueDate = moment(response.data.initialPaymentDueDate).format('MMM DD, YYYY');
              // only CIS plans have nextPaymentDate, hide on DIS plans
              this.nextPaymentDate = response.data.nextPaymentDate ? moment(response.data.nextPaymentDate).format('MMM DD, YYYY') : '';
              // frequency options is an array that contain termPeriodMonths and paymentAmount info
              // some plans have multiple termPeriodMonths/paymentAmount pairs to choose from
              this.paymentFrequencyOptions = response.data.frequencyOptions;
              this.loading = false;
              // app is ready when the details are there
              this.isReady = true;
            });
        },
        // Find the plan in which to enroll the user
        loadSelectedPlan() {
          if (this.$el.dataset.plan) {
            this.selectedPlan = this.$el.dataset.plan;

            // once we know which plan, load the details for that plan
            this.getPaymentPlanDetails();
          }
        },
        recalculatePayment() {
          this.loading = true;
          // get data ready for recalc call
          const recalcData = {
            initialPaymentAmount: parseFloat(this.initialPaymentAmount.replace(/[^0-9-.]/g, '')),
            planId: this.selectedPlan,
          };

          // show an error if the recalc amount is less than the default value
          if (recalcData.initialPaymentAmount < this.defaultPaymentAmount) {
            this.loading = false;
            createError(this.$refs.recalculate, this.$refs.recalculateError, `Must not be less than ${formatBasicCurrency(this.defaultPaymentAmount)}.`);
          } else {
            removeError(this.$refs.recalculate, this.$refs.recalculateError);
            paymentPlansApi.recalculatePaymentPlan(recalcData)
              .then((response) => {
                this.loading = false;
                // reload payment frequency from the response
                this.paymentFrequencyOptions = response.data.frequencyOptions;
              })
              .catch(() => {
                // if there's an error with recalc, show the error page to call
                this.loading = false;
                this.setStep(3);
                this.error = true;
              });
          }
        },
      },
    });
  }
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/paymentPlanEnroll.js